/**
 * External dependencies
 */
import React from "react";
import { connect } from "react-redux";
import { QueryRenderer } from "@cubejs-client/react";
import _, {
  first,
  get,
  find,
  map,
  isEmpty,
  defaults,
  split,
  uniq,
  isObject,
} from "lodash";

/**
 * Internal dependencies
 */
import {
  getFormattedDateRange,
  getCompareDateRange,
} from "state/ui/date-range/selectors";
import { getFilters } from "state/ui/dashboard-filter/selectors";
import cubejsApi from "lib/cube-js";
import { getCubeMeta } from "state/graph-cube/selectors";
import { determineGranularity } from "./utils";
import { getSelectedLocations } from "../../state/ui/location-filter/selectors";
import { getCompareType } from "../../state/ui/date-range/selectors";
import toPreviousDateRange from "utils/compare-date-range";
import { useActivePartnerProvider } from "../active-partner-provider/hooks";

// returns the first found time dimension
// cant see if there is a built in way to do this with the cube library
//
const findTimeDimension = (query, cubeMetaData, name = undefined) => {
  // step one is to find the cubes involved in this query
  let cubes = uniq(
    map([...query.measures, ...query.dimensions], (m) => first(split(m, ".")))
  );

  // check if any of the cubes are excluded from time dimensions
  for (const cubeString of cubes) {
    const cube = find(cubeMetaData.cubes, { name: cubeString });

    if (
      cube &&
      get(cube, ["meta", "exclude_from_time_dimension_filter"], false) == true
    ) {
      return null;
    }
  }

  // for each of the cubes, check if there is a time dimension
  // stop at the first one found
  let timeDimension;
  for (var i = cubes.length - 1; i >= 0; i--) {
    let cube = find(cubeMetaData.cubes, { name: cubes[i] });
    if (cube) {
      timeDimension = name
        ? find(cube.dimensions, { type: "time", name })
        : find(cube.dimensions, { type: "time" });
      if (timeDimension) {
        return timeDimension;
      }
    }
  }

  return null;
};

const QueryCubeJS = ({
  cubeMeta,
  compareDateRange,
  overrideGlobalFilterCube = false,
  filterDateRange = false,
  queryPreviousRange = false,
  applyDashboardFilters = false,
  timeSeries = false,
  timeDimension = undefined,
  dateRange,
  forceDateRange,
  timeGranularity = undefined,
  compareType = "previous",
  selectedLocations,
  ...props
}) => {
  const { partner } = useActivePartnerProvider();
  const [fetchingFilters, setFetchingFilters] = React.useState(false);
  const [filters, setFilters] = React.useState([]);

  let queries = {};
  let query = defaults({}, props.query, {
    filters: [],
    timezone: partner.timeZone ?? "Australia/Melbourne",
  });

  let normalizedTimeDimension = findTimeDimension(query, cubeMeta);

  // replace all time dimensions with the dashboard dimensions
  // caller must explictly state if there is granularity
  if (normalizedTimeDimension && filterDateRange === true) {
    if (normalizedTimeDimension) {
      let _timeDimensions =
        timeDimension && isObject(timeDimension)
          ? [
              {
                granularity: timeGranularity
                  ? timeGranularity
                  : timeSeries
                    ? determineGranularity(dateRange)
                    : null,
                ...timeDimension,
              },
            ]
          : [
              {
                dimension: timeDimension || normalizedTimeDimension.name,
                dateRange: dateRange,
                granularity: timeGranularity
                  ? timeGranularity
                  : timeSeries
                    ? determineGranularity(dateRange)
                    : null,
              },
            ];
      query = { ...query, timeDimensions: _timeDimensions };
    }
  } else if (timeDimension) {
    query = {
      ...query,
      timeDimensions: [
        {
          dimension: timeDimension,
          dateRange: forceDateRange,
          granularity: timeGranularity,
        },
      ],
    };
  }

  // replace all default filters with the dashboard filters
  if (true === applyDashboardFilters && !isEmpty(props.filters)) {
    const measuresContainsDisabled = query.measures.some((measure) => {
      // get the meta data for the measure
      const measureMetaData = find(cubeMeta.cubes, {
        name: measure.split(".")[0],
      });

      if (
        !measureMetaData ||
        measureMetaData.meta.disable_global_filter === true
      ) {
        return true;
      }
      return false;
    });

    if (false == measuresContainsDisabled) {
      // this is mostly a hack to force some reports to use their own version
      // of filters
      let gFilters = map(props.filters, (f) => {
        if (overrideGlobalFilterCube) {
          let splitMember = split(f.member, ".");
          return {
            ...f,
            member: `${overrideGlobalFilterCube}.${splitMember[1]}`,
          };
        }
        return f;
      });

      query.filters = [...query.filters, ...gFilters];
    }
  }

  if (selectedLocations?.length > 0) {
    query.filters = [
      ...query.filters,
      {
        member: "Location.location_key",
        operator: "in",
        values: selectedLocations,
      },
    ];
  } else {
  }

  React.useEffect(() => {
    if (
      query.dimensions &&
      query.dimensions.length > 0 &&
      query.timeDimensions &&
      query.timeDimensions.length > 0 &&
      query.timeDimensions[0].granularity != null
    ) {
      setFetchingFilters(true);
      // there is no way to limit the time dimensions with granualarity without also
      // limiting the dimensions. So we need to run a query first to
      // get the limited dimensions

      (async () => {
        try {
          let _query = {
            ...query,
            limit: 10,
            timeDimensions: map(query.timeDimensions, (t) => ({
              ...t,
              granularity: null,
            })),
          };

          let response = await cubejsApi.load(_query);
          const data = response.loadResponse?.results?.[0]?.data ?? [];

          let newFilters = [];

          for (const d of query.dimensions) {
            let f = {
              member: d,
              operator: "equals",
              values: [],
            };
            for (const [key, value] of Object.entries(data)) {
              f.values.push(value[d]);
            }

            if (f.values.length > 0) {
              newFilters.push(f);
            }
          }

          setFilters([...query.filters, ...newFilters]);
          setFetchingFilters(false);
        } catch (err) {}
      })();
    }
  }, []);

  if (fetchingFilters) {
    return null;
  }

  query = {
    ...query,
    filters: [...query.filters, ...filters],
  };

  queries = {
    main: {
      ...query,
    },
  };

  if (queryPreviousRange) {
    // since version 0.20 you can use a "compareDateRange" to compare dates
    let _originalTimeDimension = queries.main.timeDimensions?.[0];

    if (_originalTimeDimension && _originalTimeDimension.dateRange) {
      let _compareTimeDimension = { ...queries.main.timeDimensions[0] };

      // since we added the ability to set cutom date ranges, we need to
      // be able to generate the compare time on the fly
      let _compareDateRange = {
        dimension: _compareTimeDimension.dimension,
        granularity: _compareTimeDimension.granularity,
        compareDateRange: [
          _originalTimeDimension.dateRange,
          toPreviousDateRange(_originalTimeDimension.dateRange, compareType),
        ],
      };

      queries.main = {
        ...queries.main,
        timeDimensions: [_compareDateRange],
      };
    }
  }

  return (
    <QueryRenderer
      queries={queries}
      cubeApi={cubejsApi}
      render={props.render}
      renewQuery={false}
    />
  );
};

export default connect((state, ownProps) => ({
  compareType: getCompareType(state),
  dateRange: getFormattedDateRange(state),
  compareDateRange: ownProps.compareDateRange
    ? ownProps.compareDateRange
    : getCompareDateRange(state),
  filters: getFilters(state),
  cubeMeta: getCubeMeta(state),
  selectedLocations: getSelectedLocations(state),
}))(QueryCubeJS);
